import { useQuery } from '@tanstack/react-query';
import { qk } from '../queryKeys';
import { queryClient } from '../queryClient';
import { seedEntities, useEntity, patchEntity } from '../entities';
// backOfficeApi is still untyped JS (allowJs); cast the loaders to a permissive
// signature at this seam so callers get clean types until the loaders are typed.
import * as api from '../backOfficeApi';

type LoaderOpts = { signal?: AbortSignal; [k: string]: unknown };
const loadEventPages = api.loadEventPages as (opts?: LoaderOpts) => Promise<any>;
const loadEventStatsForEvents = api.loadEventStatsForEvents as (ids: string[], opts?: LoaderOpts) => Promise<any>;

export const EVENT_PAGE_LIMIT = 30;

type EventRow = { id: string; [k: string]: unknown };
type EventListResult = { rows: EventRow[]; page: number; hasMore: boolean; total?: number; source?: string };

/**
 * The shared events list (page 1). Every surface that needs "the events" — the
 * Events page, the event-filter pickers, payout/order/finance event dropdowns —
 * calls this same hook, so the list is fetched ONCE and reused from cache for
 * `staleTime` instead of refetched per surface.
 */
export function useEventsList(opts: { enabled?: boolean; limit?: number } = {}) {
  const { enabled = true, limit = EVENT_PAGE_LIMIT } = opts;
  return useQuery<EventListResult>({
    queryKey: qk.events.list({ page: 1, limit }),
    queryFn: async ({ signal }) => {
      const res = await loadEventPages({ page: 1, limit, includeStats: false, sort: 'upcoming', signal });
      // Sentient layer: every event the list returns becomes a per-id cached
      // entity, so opening its detail or resolving it as a reference elsewhere
      // (a payout's event, an order's event) is instant and consistent.
      seedEntities(qk.events.detail, res?.rows);
      return res;
    },
    enabled,
  });
}

/**
 * One event by id — instant when any list already seeded it (the common path:
 * you navigated from a list). Reference-only for now; cold deep-link by-id fetch
 * + normalization lands with the detail routes in Phase 2.
 */
export function useEvent(id: string | null | undefined) {
  return useEntity<EventRow>(qk.events.detail, id);
}

/**
 * Merge already-filtered LOCAL events with backend search results, deduped by id. The local list
 * comes first (instant — it's what's already on screen and must never blank while a search loads);
 * server-only matches (past/deep events not in the cached list) are appended. This is the rule:
 * search AUGMENTS the existing filtered list, it never replaces it. `serverRows` may be empty/loading.
 */
export function mergeEventMatches(localFiltered: EventRow[], serverRows?: EventRow[] | null, query?: string): EventRow[] {
  const merged = (!serverRows || serverRows.length === 0)
    ? localFiltered
    : (() => { const seen = new Set(localFiltered.map((e) => e.id)); return [...localFiltered, ...serverRows.filter((e) => e && !seen.has(e.id))]; })();
  const q = (query || '').trim().toLowerCase();
  if (!q) return merged;
  // Rank NAME/title matches above ones that only matched on venue/organizer/id — someone typing a
  // name wants the named event first, not an unrelated event held at a similarly-named venue. Stable.
  const nameHit = (e: EventRow) => (String((e as any).name || (e as any).title || '').toLowerCase().includes(q) ? 1 : 0);
  return merged.map((e, i) => [e, i] as [EventRow, number]).sort((a, b) => (nameHit(b[0]) - nameHit(a[0])) || (a[1] - b[1])).map((x) => x[0]);
}

/**
 * The ONE producer of fetch args for server-side event search/browse — shared by
 * `useEventSearch`'s initial fetch AND by callers paging past page 1 (EventsPage's
 * "Load more" while searching/past-browsing), so the sort/status logic lives in one
 * place instead of being re-derived at each call site.
 *
 * A plain `sort=desc` is unbounded — the furthest-future-dated row (a stray
 * test/draft event) sorts first and can crowd genuinely completed events off page 1.
 * `sort=past` (server-side) bounds to date < now AND orders most-recently-completed
 * first, so browsing past events (no query) actually reaches them. A text search
 * still wants every event regardless of date.
 */
export function buildEventSearchArgs(opts: {
  query?: string; includePast?: boolean; status?: string; approvalStatus?: string; page?: number;
}) {
  const { query = '', includePast = false, status, approvalStatus, page = 1 } = opts;
  const q = query.trim();
  return {
    page, limit: 50, includeStats: false,
    searchQuery: q || undefined, upcoming: false,
    sort: (!q && includePast) ? 'past' : 'desc',
    status,
    approvalStatus,
    // No includeTotal. It used to be set here for a "Showing N of TOTAL" line, but
    // /api/event-pages never returns a total — paginate() does a limit+1 look-ahead and
    // returns `hasMore` only — so the flag bought a param on the wire and nothing else,
    // while the denominator it was meant to feed silently fell back to a page size.
  } as const;
}

/**
 * Server-side event search for pickers. The shared `useEventsList` only holds page 1 sorted
 * upcoming-first, so a client-side filter over it can never reach a past event or one beyond the
 * first 30 — the exact "I had to Load more until it appeared / can't select past events" failure.
 * This hits the backend with `searchQuery` (title/slug ILIKE) and NO `upcoming` filter, so ANY
 * event — past, draft, or deep in the list — is reachable. `includePast` with an empty query loads
 * the all-events list (recent first) so past events are browsable, not only searchable.
 *
 * Only ever fetches page 1 — a caller that needs to page further (EventsPage's "Load
 * more" while search/past-browsing is active) calls `buildEventSearchArgs` directly
 * with its own `page` and manages the extra pages itself, same pattern as the base
 * upcoming list's `extraPages`.
 */
export function useEventSearch(
  opts: { query?: string; includePast?: boolean; enabled?: boolean; status?: string; approvalStatus?: string } = {},
) {
  const { query = '', includePast = false, enabled = true, status, approvalStatus } = opts;
  const q = query.trim();
  const active = enabled && (q.length >= 2 || includePast);
  return useQuery<EventListResult>({
    queryKey: ['events', 'search', q, includePast, status, approvalStatus],
    queryFn: async ({ signal }) => {
      const res = await loadEventPages({ ...buildEventSearchArgs({ query, includePast, status, approvalStatus }), signal });
      seedEntities(qk.events.detail, res?.rows);
      return res;
    },
    enabled: active,
    staleTime: 60_000,
  });
}

/**
 * Write-through an event edit into the shared events list (page 1) + entity cache
 * so every surface reading the cache (EventsPage, pickers) reflects the change
 * with no refetch. Merges, so a partial update keeps the row's other fields.
 */
export function patchEvent(next: { id: string; [k: string]: unknown }) {
  patchEntity(qk.events.list({ page: 1, limit: EVENT_PAGE_LIMIT }), qk.events.detail, next);
}

/**
 * Invalidate every events-list query so EventsPage / pickers refetch. Use after a
 * CREATE — the new event needs the server-normalized row (id, backendBacked,
 * occupancy, revenue, …) that a client-built object can't fully supply, so a
 * refetch is more correct than prepending a partial row.
 */
export function invalidateEventsList() {
  queryClient.invalidateQueries({ queryKey: ['events', 'list'] });
}

/** Per-event sales stats for a set of event ids (the slow admin-stats scan — 5min stale). */
export function useEventStats(eventIds: string[], opts: { enabled?: boolean } = {}) {
  const ids = (eventIds || []).filter(Boolean);
  return useQuery<{ stats: Record<string, unknown>; source?: string | null; error?: string | null }>({
    queryKey: qk.events.stats(ids),
    queryFn: ({ signal }) => loadEventStatsForEvents(ids, { signal }),
    enabled: ids.length > 0 && (opts.enabled ?? true),
    staleTime: 5 * 60_000,
  });
}
